import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Stack,
  Typography,
  type PopoverOrigin,
} from '@mui/material';
import { AutoAwesome as WhyIcon } from '@mui/icons-material';

// ---------------------------------------------------------------------------
// WhyAffordance — tiny "Why?" icon button that reveals one line of AI
// reasoning on click. Phase 3 task 3.3 (visible AI reasoning).
//
// Design calls:
//   * Icon-only trigger so the explanation doesn't crowd the surface it sits
//     next to. The tooltip on the trigger names what it is so the icon is
//     still discoverable.
//   * Popover (not Tooltip) on click so the reasoning is sticky enough to
//     read and copy; hover alone would dismiss too easily.
//   * Renders nothing when the rationale is empty, so callers can pass
//     `rationale` freely without guarding at every call site.
// ---------------------------------------------------------------------------

interface WhyAffordanceProps {
  /** The reasoning line. If empty / whitespace, nothing renders. */
  rationale?: string | null;
  /** Optional secondary line (e.g. strategy link, confidence band). */
  secondary?: string | null;
  /** Button size. Compact is used on dense rows like the PriorityList. */
  size?: 'compact' | 'default';
  /** Aria label. Defaults to "Why this decision?". */
  label?: string;
  /** Popover anchor override (defaults to origin bottom-left). */
  anchorOrigin?: PopoverOrigin;
}

export default function WhyAffordance({
  rationale,
  secondary,
  size = 'default',
  label = 'Why this decision?',
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
}: WhyAffordanceProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!rationale || rationale.trim().length === 0) return null;

  const iconSize = size === 'compact' ? 14 : 16;
  const buttonPadding = size === 'compact' ? 0.25 : 0.5;

  return (
    <>
      <IconButton
        size="small"
        aria-label={label}
        title={label}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          p: buttonPadding,
          color: 'primary.main',
          opacity: 0.8,
          '&:hover': { opacity: 1, bgcolor: 'rgba(108, 155, 255, 0.08)' },
        }}
      >
        <WhyIcon sx={{ fontSize: iconSize }} />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={anchorOrigin}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              p: 1.5,
              maxWidth: 360,
              borderRadius: 2,
              borderLeft: 3,
              borderColor: 'primary.main',
            },
          },
        }}
      >
        <Stack spacing={0.75}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              letterSpacing: '0.12em',
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            AI reasoning
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {rationale}
          </Typography>
          {secondary && secondary.trim().length > 0 && (
            <Box sx={{ pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {secondary}
              </Typography>
            </Box>
          )}
        </Stack>
      </Popover>
    </>
  );
}
